/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Storytelling (C) Copyright IBM Corp. 2014, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'baglass/core-client/js/core-client/ui/core/Class'
], function(Class) {

	const NavigationController = Class.extend({

		/**
		 * @param {Object} options A normal object of properties
		 * @param {Object} options.transitionController An instance of a class whose base class is a TransitionController
		 * @param {Object} options.sceneLoader An instance of the SceneLoader class
		 * @param {Object} options.logger The logging class
		 * @param {Object} options.sceneLayoutApi The SceneLayout API
		 *
		 * @see TransitionController.js
		 * @see SceneLoader.js
		 * @see SceneLayout.js
		 */
		init: function(options) {
			NavigationController.inherited('init', this, arguments);

			this._transitionController = options.transitionController;
			this._sceneLoader = options.sceneLoader;

			this.logger = options.logger;
			this.sceneLayoutApi = options.sceneLayoutApi; // Required

			this.inProgress = false;
		},

		/**
		 * @param {Object} options
		 * @param {Number} options.index (Deprecated) This value is still used by StoryPaneController but should be avoided for new uses
		 * @param {String} options.modelId This is the layout model id and should be used over an index value
		 * @param {Function} [options.onTargetSelected] An optional function that, when provided, will trigger a 'navigation:started' event
		 * @param {Function} [options.refreshData] An optional parameter that, when provided, will trigger a refresh of all the story data widgets
		 */
		jumpTo: function(options) {
			if(this.inProgress) {
				return Promise.reject();
			}
			this.inProgress = true;

			// TODO: Update StoryPaneController to use sceneIds instead of index values
			const targetSceneByIndex = this.sceneLayoutApi.getSceneByIndex(options.index);
			const targetScene = targetSceneByIndex || this.sceneLayoutApi.getSceneById(options.modelId);
			const currentScene = this.sceneLayoutApi.getCurrentScene() || targetScene;

			const onDeck = this.sceneLayoutApi.getAdjacentScenes(targetScene).concat([targetScene]);
			const offDeck = this.sceneLayoutApi.getAdjacentScenes(currentScene).concat([currentScene]);

			return this._sceneLoader.waitForSceneToLoad(targetScene, options).then(() => {
				// start pre-loading of the next scenes.
				this._sceneLoader.startBackgroundLoad(this._generateShiftedSceneArray(targetScene), options);
				this._transitionController.deckScenes(onDeck);
				this._updateSelection(currentScene, targetScene);
				if(options && options.onTargetSelected) {
					options.onTargetSelected({
						scene: targetScene,
						play: options.play
					});
				}
				return this._transitionController.jumpTo(currentScene, targetScene);
			}).then((result) => {
				this._transitionController.undeckScenes(offDeck);
				this.inProgress = false;
				return {
					scene: targetScene,
					overview: result.overview
				};
			}).catch((error) => {
				this.logger.error('NavigationController.jumpTo()', error);
				this.inProgress = false;
				return Promise.reject();
			});
		},

		nextPage: function(options) {
			if(this.inProgress) {
				return Promise.reject();
			}
			this.inProgress = true;

			let targetScene = this.sceneLayoutApi.getSceneByIndex(options.to) || this.sceneLayoutApi.getNextScene();
			let currentScene = this.sceneLayoutApi.getCurrentScene() || targetScene;

			const onDeck = this.sceneLayoutApi.getAdjacentScenes(targetScene).concat([targetScene]);
			const offDeck = this.sceneLayoutApi.getAdjacentScenes(currentScene).concat([currentScene]);

			return this._sceneLoader.waitForSceneToLoad(targetScene, options).then(() => {
				if(options.refreshData) {
					this._sceneLoader.startBackgroundLoad(this._generateShiftedSceneArray(targetScene), options);
				}
				this._transitionController.deckScenes(onDeck);
				this._updateSelection(currentScene, targetScene);
				if (options && options.onTargetSelected) {
					options.onTargetSelected({ scene: targetScene });
				}
				return this._transitionController.forward(currentScene, targetScene);
			}).then((result) => {
				this._transitionController.undeckScenes(offDeck);
				this.inProgress = false;
				return {
					scene: targetScene,
					overview: result.overview
				};
			}).catch((error) => {
				this.logger.error('NavigationController.nextPage()', error);
				this.inProgress = false;
				return Promise.reject();
			});
		},

		previousPage: function(options) {
			if(this.inProgress) {
				return Promise.reject();
			}
			this.inProgress = true;

			let targetScene = this.sceneLayoutApi.getSceneByIndex(options.to) || this.sceneLayoutApi.getPreviousScene();
			let currentScene = this.sceneLayoutApi.getCurrentScene() || targetScene;

			const onDeck = this.sceneLayoutApi.getAdjacentScenes(targetScene).concat([targetScene]);
			const offDeck = this.sceneLayoutApi.getAdjacentScenes(currentScene).concat([currentScene]);

			return this._sceneLoader.waitForSceneToLoad(targetScene, options).then(() => {
				if(options.refreshData) {
					this._sceneLoader.startBackgroundLoad(this._generateShiftedSceneArray(targetScene), options);
				}
				this._transitionController.deckScenes(onDeck);
				this._updateSelection(currentScene, targetScene);
				if (options && options.onTargetSelected) {
					options.onTargetSelected({ scene: targetScene });
				}
				return this._transitionController.backward(currentScene, targetScene);
			}).then((result) => {
				this._transitionController.undeckScenes(offDeck);
				this.inProgress = false;
				return {
					scene: targetScene,
					overview: result.overview
				};
			}).catch((error) => {
				this.logger.error('NavigationController.previousPage()', error);
				this.inProgress = false;
				return Promise.reject();
			});
		},

		/**
		 * FIXME: This method should probably be within SceneLayout.js / part of the SceneLayout API
		 */
		_updateSelection: function(from, to) {
			from.$el.removeClass('selected');
			to.$el.addClass('selected');
		},

		/**
		 * Create a new ordered array of all scenes in sequential order starting from the provided scene
		 * Example:
		 * array = [1, 2, 3, 4, 5, 6]
		 * currentIndex = 2 (value of 3)
		 * newArray = [3, 4, 5, 6, 1]
		 *
		 * @param {Object} scene The scene to use as the starting scene of the new array
		 */
		_generateShiftedSceneArray: function(scene) {
			const _inRange = function(value, array) {
				return (value >= 0 && value < array.length);
			};

			const firstScene = this.sceneLayoutApi.getNextScene(scene);
			const scenes = this.sceneLayoutApi.getScenes();
			let index = this.sceneLayoutApi.getSceneIndex(firstScene);
			index = _inRange(index, scenes) ? index : 0;

			let orderedScenes = [];
			for (let i = 0; i < scenes.length; i++) {
				let scene = scenes[(i + index) % scenes.length];
				orderedScenes.push(scene);
			}

			if(orderedScenes[orderedScenes.length - 1].id === scene.id) {
				orderedScenes.pop();
			}
			return orderedScenes;
		},

		/**
		 * Used when a story type (like Slideshow) has multiples transition types
		 */
		setTransitionController: function(transitionController) {
			delete this._transitionController;
			this._transitionController = transitionController;
		}
	});

	return NavigationController;
});
